import type { Dispatch, SetStateAction } from 'react'
import TimeSlider from '../Sliders/TimeSlider'

type DoserSettingsProps = {
    advancedOptionsState: Record<string, unknown>
    setAdvancedOptionsState: Dispatch<SetStateAction<Record<string, unknown>>>
}

const DoserSettings = ({ advancedOptionsState, setAdvancedOptionsState }: DoserSettingsProps) => {
    const doseAmount = Number(
        (advancedOptionsState as { dose_amount?: number }).dose_amount ?? 10
    )

    return (
        <div className="space-y-6">
            <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-3 border-b border-zinc-700 pb-2 text-sm font-medium text-zinc-200">
                    Amount per dose
                </h3>
                <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                    Milliliters per dose
                </label>
                <input
                    type="number"
                    min={1}
                    max={50}
                    value={Number.isFinite(doseAmount) ? doseAmount : ''}
                    onChange={(e) => {
                        const v = e.target.value === '' ? 10 : Number(e.target.value)
                        const next = Number.isFinite(v) ? Math.min(50, Math.max(1, v)) : 10
                        setAdvancedOptionsState((prev) => ({
                            ...(prev as object),
                            dose_amount: next,
                        }))
                    }}
                    className="mt-1 w-full max-w-[12rem] rounded-md border border-zinc-600 bg-zinc-950 px-3 py-2 text-zinc-50"
                />
            </section>

            <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-3 border-b border-zinc-700 pb-2 text-sm font-medium text-zinc-200">
                    Time between nutrients
                </h3>
                <p className="mb-3 text-xs text-zinc-500">
                    Wait after each part of the dose before adding the next nutrient.
                </p>
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <TimeSlider
                        type="dose"
                        time="hour"
                        defaultValue={
                            (advancedOptionsState as { time_between_doses?: { hour?: number } })
                                .time_between_doses?.hour ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                    <TimeSlider
                        type="dose"
                        time="min"
                        defaultValue={
                            (advancedOptionsState as { time_between_doses?: { minute?: number } })
                                .time_between_doses?.minute ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                    <TimeSlider
                        type="dose"
                        time="sec"
                        defaultValue={
                            (advancedOptionsState as { time_between_doses?: { seconds?: number } })
                                .time_between_doses?.seconds ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                </div>
            </section>

            <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-3 border-b border-zinc-700 pb-2 text-sm font-medium text-zinc-200">
                    Time before re-adjusting
                </h3>
                <p className="mb-3 text-xs text-zinc-500">
                    Minimum delay before the controller re-checks and doses again toward targets.
                </p>
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <TimeSlider
                        type="readjust"
                        time="hour"
                        defaultValue={
                            (advancedOptionsState as { time_before_readjustment?: { hour?: number } })
                                .time_before_readjustment?.hour ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                    <TimeSlider
                        type="readjust"
                        time="min"
                        defaultValue={
                            (advancedOptionsState as { time_before_readjustment?: { minute?: number } })
                                .time_before_readjustment?.minute ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                    <TimeSlider
                        type="readjust"
                        time="sec"
                        defaultValue={
                            (advancedOptionsState as { time_before_readjustment?: { seconds?: number } })
                                .time_before_readjustment?.seconds ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                </div>
            </section>

            <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-3 border-b border-zinc-700 pb-2 text-sm font-medium text-zinc-200">
                    Mixing time
                </h3>
                <p className="mb-3 text-xs text-zinc-500">
                    How long to circulate after dosing before the next measurement.
                </p>
                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <TimeSlider
                        type="mix"
                        time="min"
                        defaultValue={
                            (advancedOptionsState as { mixing_time?: { minute?: number } }).mixing_time
                                ?.minute ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                    <TimeSlider
                        type="mix"
                        time="sec"
                        defaultValue={
                            (advancedOptionsState as { mixing_time?: { seconds?: number } }).mixing_time
                                ?.seconds ?? 0
                        }
                        advancedOptionsState={advancedOptionsState}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                </div>
            </section>
        </div>
    )
}

export default DoserSettings
